import RequiredMark from './RequiredMark';
import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { flagEmoji, countryDisplayName, type PhonePrefix } from '../lib/countries';
import { NATIONAL_PHONE_MAX_DIGITS, choosePhonePrefix, composePhone, splitPhone, typeNationalNumber } from '../lib/countryRegistryRules';
import { useCountryRegistry } from '../hooks/useCountryRegistry';
import { Icon } from './Icon';

/**
 * Self-contained phone-number field: a country-prefix picker on the left
 * (with flag + dial-in code, search by code or country name) and a
 * national-number input on the right, sharing a single rounded border so
 * the whole thing reads as one control.
 *
 * Stores the value as the concatenation ``prefix + nationalNumber`` (no
 * separators), e.g. ``+34600000000``.
 *
 * The prefixes come from the platform's country registry
 * (`useCountryRegistry`). While it loads the list is empty and a stored number
 * is shown verbatim (see `splitPhone`); it is split again once the list lands.
 * Without a list (loading, or the load failed) the number is read-only: there
 * is no country code to put in front of what the user would type.
 *
 * A stored ``+…`` number whose code the registry does not list is also shown
 * whole, with no prefix picked. Editing it keeps its ``+``; nothing is emitted
 * until the user edits, so an untouched number is never rewritten.
 *
 * Lives in @shared so both apps use one implementation. It is i18n-agnostic:
 * the two visible strings (`searchPlaceholder`, `noResultsLabel`) and the
 * `locale` used for country names arrive as props. The tenant wraps it with
 * its `useLocale()` values; the admin (Spanish-only) relies on the defaults.
 */

export interface PhoneFieldProps {
    /** Persisted phone string (e.g. ``+34600000000``). */
    value: string | null;
    /** Called with the concatenated string whenever prefix or national digits change. */
    onChange: (next: string) => void;
    /** Floating label rendered above the field. */
    label: string;
    /** Required field: red asterisk after the label (Standards §14.7). */
    markRequired?: boolean;
    /** Placeholder shown in the national-number input. */
    placeholder?: string;
    /** Restrict the national-number length (digits only). */
    maxLength?: number;
    /**
     * Full BCP-47 tag used to render country names in the picker (``pt-PT``,
     * not the bare UI code: ``pt`` gives Brazilian names such as «Polônia»).
     * Defaults to ``es-ES``.
     */
    locale?: string;
    /** Search-box placeholder inside the prefix picker. */
    searchPlaceholder?: string;
    /** Empty-state label when the search matches no country. */
    noResultsLabel?: string;
}

const PhoneField: React.FC<PhoneFieldProps> = ({
    value,
    onChange,
    label,
    markRequired = false,
    placeholder = '600 000 000',
    maxLength = NATIONAL_PHONE_MAX_DIGITS,
    locale = 'es-ES',
    searchPlaceholder = '🔎 +34, ES, España…',
    noResultsLabel = 'Sin resultados',
}) => {
    const { phonePrefixes, loading } = useCountryRegistry();
    const initial = useMemo(() => splitPhone(value, phonePrefixes), [value, phonePrefixes]);
    const [prefix, setPrefix] = useState(initial.prefix);
    const [national, setNational] = useState(initial.national);
    const [open, setOpen] = useState(false);

    // Re-sync the internal split when the parent feeds a new value (typical
    // after a server-roundtrip refresh) or when the prefix list arrives.
    useEffect(() => {
        setPrefix(initial.prefix);
        setNational(initial.national);
    }, [initial]);

    const apply = (next: { prefix: string; national: string }) => {
        setPrefix(next.prefix);
        setNational(next.national);
        onChange(composePhone(next.prefix, next.national));
    };

    return (
        <div className="relative pt-2">
            <span className="absolute top-0 left-2 px-1 bg-white dark:bg-surface-dark text-[10px] font-bold text-primary uppercase tracking-wider z-10 pointer-events-none">
                {label}
                {markRequired && <> <RequiredMark /></>}
            </span>
            <div className="flex items-center w-full h-[42px] rounded-md border bg-transparent border-border-strong dark:border-border-strong-dark overflow-hidden focus-within:ring-1 focus-within:ring-primary focus-within:border-primary transition-all shadow-sm hover:border-border-strong dark:hover:border-border-strong-dark">
                <PrefixPicker
                    prefixes={phonePrefixes}
                    value={prefix}
                    onChange={(next) => apply(choosePhonePrefix(next, national))}
                    open={open}
                    setOpen={setOpen}
                    locale={locale}
                    searchPlaceholder={searchPlaceholder}
                    noResultsLabel={noResultsLabel}
                />
                <div className="w-[1px] h-6 bg-border-subtle dark:bg-border-subtle-dark mx-1"></div>
                <div className="relative flex-1 flex items-center h-full">
                    <Icon name="call" className="absolute left-2 text-slate-400 !text-[18px]" />
                    {/* architecture-check-disable-next-line — composite phone field shares the parent's border */}
                    <input
                        type="tel"
                        value={national}
                        onChange={(e) => apply(typeNationalNumber(e.target.value, prefix, phonePrefixes, maxLength))}
                        placeholder={placeholder}
                        // No prefix list (still loading, or the load failed): the
                        // stored number is shown whole, and anything typed would
                        // be saved with no country code in front.
                        disabled={loading || phonePrefixes.length === 0}
                        className="w-full h-full bg-transparent pl-8 pr-3 py-0 text-sm text-text-main dark:text-white outline-none border-none focus:ring-0 placeholder-slate-400 dark:placeholder-slate-600"
                        inputMode="numeric"
                    />
                </div>
            </div>
        </div>
    );
};

/* ── PrefixPicker — inline popover that mimics DropdownSelect's panel ── */

interface PrefixPickerProps {
    prefixes: PhonePrefix[];
    value: string;
    onChange: (code: string) => void;
    open: boolean;
    setOpen: (open: boolean) => void;
    locale: string;
    searchPlaceholder: string;
    noResultsLabel: string;
}

const PrefixPicker: React.FC<PrefixPickerProps> = ({ prefixes, value, onChange, open, setOpen, locale, searchPlaceholder, noResultsLabel }) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [search, setSearch] = useState('');
    const [rect, setRect] = useState<{ top: number; left: number; flip: boolean } | null>(null);

    const current = useMemo<PhonePrefix | undefined>(
        () => prefixes.find(p => p.code === value),
        [prefixes, value],
    );

    useEffect(() => {
        if (!open) return;
        const onMouseDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (triggerRef.current?.contains(t)) return;
            if (panelRef.current?.contains(t)) return;
            setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, setOpen]);

    useLayoutEffect(() => {
        if (!open || !triggerRef.current) return;
        const update = () => {
            const r = triggerRef.current!.getBoundingClientRect();
            const spaceBelow = window.innerHeight - r.bottom;
            const flip = spaceBelow < 320 && r.top > 320;
            setRect({
                top: flip ? r.top - 8 : r.bottom + 8,
                left: r.left,
                flip,
            });
        };
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [open]);

    useEffect(() => {
        if (open) setSearch('');
    }, [open]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return prefixes;
        return prefixes.filter(p => {
            const name = countryDisplayName(p.cc, locale).toLowerCase();
            return p.code.toLowerCase().includes(q) || name.includes(q) || p.cc.toLowerCase().includes(q);
        });
    }, [prefixes, search, locale]);

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                onClick={() => setOpen(!open)}
                className="h-full pl-3 pr-2 bg-transparent text-sm text-text-main dark:text-white outline-none border-none flex items-center gap-1.5 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors cursor-pointer"
            >
                <span className="text-base leading-none" aria-hidden>{current ? flagEmoji(current.cc) : '🌐'}</span>
                <span>{value}</span>
                <Icon name="expand_more" className={`!text-[16px] text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && rect && createPortal(
                <div
                    ref={panelRef}
                    style={{
                        position: 'fixed',
                        top: rect.flip ? undefined : rect.top,
                        bottom: rect.flip ? window.innerHeight - rect.top : undefined,
                        left: rect.left,
                    }}
                    className="z-[200] w-72 bg-white dark:bg-surface-dark rounded-xl shadow-xl border border-border-subtle dark:border-border-subtle-dark overflow-hidden animate-in fade-in zoom-in-95 duration-200"
                >
                    <div className="p-2 border-b border-border-subtle dark:border-border-subtle-dark">
                        {/* architecture-check-disable-next-line — popover-local search input */}
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={searchPlaceholder}
                            className="w-full px-3 py-1.5 bg-slate-50 dark:bg-surface-dark border border-border-subtle dark:border-border-subtle-dark rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-black dark:text-white"
                            autoFocus
                        />
                    </div>
                    <div className="max-h-72 overflow-y-auto p-1.5">
                        {filtered.length === 0 ? (
                            <div className="p-3 text-center text-xs text-slate-400">{noResultsLabel}</div>
                        ) : (
                            <div className="flex flex-col gap-0.5">
                                {filtered.map(p => {
                                    const isSelected = p.code === value && p.cc === current?.cc;
                                    return (
                                        <button
                                            key={`${p.cc}-${p.code}`}
                                            type="button"
                                            onClick={() => { onChange(p.code); setOpen(false); }}
                                            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                                                isSelected
                                                    ? 'bg-primary/10 text-primary'
                                                    : 'text-text-main dark:text-white hover:bg-slate-50 dark:hover:bg-white/5'
                                            }`}
                                        >
                                            <span className="text-base leading-none" aria-hidden>{flagEmoji(p.cc)}</span>
                                            <span className="font-mono w-12 shrink-0">{p.code}</span>
                                            <span className="flex-1 truncate text-slate-500 dark:text-slate-400">
                                                {countryDisplayName(p.cc, locale)}
                                            </span>
                                            {isSelected && (
                                                <Icon name="check" className="!text-[16px]" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
};

export default PhoneField;
